import styles from './CartOrderForm.module.scss';
import globalStyles from '../../../global-styles.module.scss';
import BuildingSitesSelector from '../building-sites/building-sites-selector';
import { useEffect, useState } from 'react';
import { IBuildingSiteListViewDto } from '../../../components/order/interfaces/building-site-list-view.interface';
import { Utils } from '../../../shared/constants/utilities.constants';
import { htmlId } from './cart-order-form.service';
import {
    mapAndGetInitialFormValues,
    removeAll,
    updateOrAddBuildingSite,
    updateOrAddBuildingSiteAddress,
    updateOrAddBuyerContact,
    updateShoppingCartDetails,
} from '../../../components/shopping-cart/services/shopping-cart.service';
import { useBetween } from 'use-between';
import { ShoppingCartUseBetween } from '../../../components/order/use-between/shopping-cart-use-between';
import { IShopingCart } from '../../../components/order/interfaces/shopping-cart.interface';
import CartBasketTable from '../cart-basket-table/CartBasketTable';
import { Form, Formik, FormikErrors } from 'formik';
import { CartOrderFormViewModel } from './cart-order-form-view.model';
import * as yup from 'yup';
import { useTranslation } from '../../../i18n/useTranslation';
import { Validator } from '../../../shared/constants/validator';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { PRODUCT_FIRST_LEVEL_PATHS, ORDER_PATHS } from '../../../shared/constants/breadcrumb.constants';
import { IShopingCartDetail } from '../../../components/order/interfaces/shopping-cart-detail.interface';
import { getEditedBuildingSite, getLinkToBuildingSitesForm } from '../building-sites/building-sites-form.service';
import { PermissionsUseBetween } from '../../../shared/use-between/permissions-use-between';
import { ErpClaimCode } from '../../../shared/models/erp-claim-code.enum';
import ErrorAlert from '../../../shared/alerts/error-alert';
import { getCommonErrorTranslate } from '../../../configuration/axios-configuration/axios.erros.interceptor.service';
import { claimIsActive } from '../../../shared/services/claims.service';
import { IBuildingSiteAddressListView } from '../../../components/order/interfaces/building-site-address-list-view.interface';
import { IBuyerContactListView } from '../../../components/order/interfaces/buyer-contact-list-view.interface';
import { Box, Checkbox, FormControlLabel, InputAdornment, TextField, Typography } from '@mui/material';
import React from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import ButtonGroupList, { ButtonItem } from '../../../shared/button-group-list/button-group-list';
import EditIcon from '@mui/icons-material/Edit';
import { UserDetailsUseBetween } from '../../../shared/use-between/user-details-use-between';
import { BuildingSiteFormViewModel } from '../building-sites/building-sites-form-view.model';
import BuildingSitesListSkeleton from '../../building-sites-list/building-sites-list-skeleton';
import BuildingSitesDeliveryContactSelector from '../building-sites/building-site-delivery-contact-selector';
import BuildingSitesDeliveryAddressSelector from '../building-sites/building-site-delivery-address-selector';
import { getProcurementExport } from '../../../services/procurement/procurement.service';

const CartOrderForm: React.FC = () => {
    const { t } = useTranslation('translation');
    const [buildingSiteSelectorIsOpen, setOpenBuildingSiteSelector] = useState(false);
    const [buildingSiteAddressesSelectorIsOpen, setOpenBuildingSiteAddressesSelector] = useState(false);
    const [buyerContactSelectorIsOpen, setOpenBuyerContactSelector] = useState(false);
    const { setShoppingCart } = useBetween(ShoppingCartUseBetween);
    const { isReadyToOrder, setIsReadyToOrder } = useBetween(ShoppingCartUseBetween);
    const { setBuildingSite } = useBetween(ShoppingCartUseBetween);
    const { buildingSiteAddress, setBuildingSiteAddress } = useBetween(ShoppingCartUseBetween);
    const { buyerContact, setBuyerContact } = useBetween(ShoppingCartUseBetween);
    const showError = (message: string) => toast.error(message);
    const navigate = useNavigate();
    const { isSelfPickup, setIsSelfPickup } = useBetween(ShoppingCartUseBetween);
    const { initialFormValues, setInitialFormValues } = useBetween(ShoppingCartUseBetween);
    const { claims } = useBetween(PermissionsUseBetween);
    const [permissionAlertIsOpen, setOpenPermissionAlert] = useState(false);
    const { userSelection } = useBetween(UserDetailsUseBetween);
    const [selectedBuildingSite, setSelectedBuildingSite] = useState<IBuildingSiteListViewDto>();
    const [isSkeletonLoader, setIsSkeletonLoader] = useState<boolean>(true);
    const [procurementHookUrl] = useState(sessionStorage.getItem('procurementHookUrl'));

    const isProcurement = !!procurementHookUrl;

    const actionButtons: ButtonItem[] = [
        { id: `${htmlId}-add-building-sites-btn`, label: t('ADD_BUILDING_SITE') },
        { id: `${htmlId}-continue-shopping-btn`, label: t('CONTINUE_SHOPPING') },
        isProcurement
            ? { id: `${htmlId}-export-btn`, label: t('EXPORT') }
            : { id: `${htmlId}-order-btn`, label: t('ORDER') },
    ];

    useEffect(() => {
        if (userSelection && userSelection.buildingSiteUuid && userSelection.buildingSiteUuid !== Utils.defaultUuid) {
            setIsSkeletonLoader(true);
            handlGetSlectedBulidingSite(userSelection.buildingSiteUuid);
        } else {
            setIsSkeletonLoader(false);
        }
    }, [userSelection]);

    const handlGetSlectedBulidingSite = (contractorUuid: string) => {
        getEditedBuildingSite(contractorUuid).then((response) => {
            if (response) {
                const model: BuildingSiteFormViewModel = response.data;
                setSelectedBuildingSite({
                    code: model.code,
                    name: model.name,
                    postalCode: model.postalCode,
                    city: model.city,
                    street: model.street,
                    countryName: model.countryName,
                } as IBuildingSiteListViewDto);
                setIsSkeletonLoader(false);
            }
        });
    };

    const handleActionButtonClick = (id: string, errors: FormikErrors<CartOrderFormViewModel>) => {
        switch (id) {
            case `${htmlId}-continue-shopping-btn`:
                navigate(PRODUCT_FIRST_LEVEL_PATHS[1].link);
                break;
            case `${htmlId}-order-btn`:
                order(errors);
                break;
            case `${htmlId}-export-btn`:
                order(errors);
                break;
            case `${htmlId}-add-building-sites-btn`:
                navigate(getLinkToBuildingSitesForm());
                break;
        }
    };

    const handleIsSelfPickup = (event: any, values: CartOrderFormViewModel) => {
        setIsSelfPickup(event.target.checked);
        handlUpdateOrAddDetails(values, event.target.checked);
    };

    const handleCloseBuildigSiteDialog = (selectedBuildingSite: IBuildingSiteListViewDto | null) => {
        if (!Utils.isNullOrUndefined(selectedBuildingSite)) {
            handlUpdateOrAddBuildingSite(selectedBuildingSite as IBuildingSiteListViewDto);
        }
        setOpenBuildingSiteSelector(false);
    };

    const handleCloseBuildigSiteAddressDialog = (selectedBuildingSiteAdress: IBuildingSiteAddressListView | null) => {
        if (!Utils.isNullOrUndefined(selectedBuildingSiteAdress)) {
            handlUpdateOrAddBuildingSiteAddress(selectedBuildingSiteAdress as IBuildingSiteAddressListView);
        }
        setOpenBuildingSiteAddressesSelector(false);
    };

    const handleCloseBuyerContactDialog = (buyerContact: IBuyerContactListView | null) => {
        if (!Utils.isNullOrUndefined(buyerContact)) {
            handlUpdateOrBuyerContact(buyerContact as IBuyerContactListView);
        }
        setOpenBuyerContactSelector(false);
    };

    const handleResponse = (shopingCart: IShopingCart) => {
        setShoppingCart(shopingCart);
        setBuildingSite(shopingCart.buildingSite);
        setBuildingSiteAddress(shopingCart.buildingSiteAddress);
        setBuyerContact(shopingCart.buyerContact);
        setIsReadyToOrder(shopingCart.isReadyToOrder);
        setInitialFormValues(mapAndGetInitialFormValues(shopingCart.detail));
        setIsSelfPickup(shopingCart.detail?.isSelfPickup);
    };

    const handlUpdateOrAddBuildingSite = (selectedBuildingSite: IBuildingSiteListViewDto) => {
        updateOrAddBuildingSite(selectedBuildingSite).then((response) => {
            if (response) {
                handleResponse(response.data);
            }
        });
    };

    const handlUpdateOrAddBuildingSiteAddress = (selectedBuildingSiteAdress: IBuildingSiteAddressListView) => {
        updateOrAddBuildingSiteAddress(selectedBuildingSiteAdress).then((response) => {
            if (response) {
                handleResponse(response.data);
            }
        });
    };

    const handlUpdateOrBuyerContact = (buyerContact: IBuyerContactListView) => {
        updateOrAddBuyerContact(buyerContact).then((response) => {
            if (response) {
                handleResponse(response.data);
            }
        });
    };

    const handlUpdateOrAddDetails = (values: CartOrderFormViewModel, isSelfPickup: boolean) => {
        updateShoppingCartDetails({
            isSelfPickup: isSelfPickup,
            reference: values.reference,
            comments: values.comments,
        } as IShopingCartDetail).then((response) => {
            if (response) {
                handleResponse(response.data);
            }
        });
    };

    const order = (errors: FormikErrors<CartOrderFormViewModel>): void | Promise<any> => {
        if (!claimIsActive(claims, ErpClaimCode.CUSTOMER_ECOM_ROLE)) {
            setOpenPermissionAlert(true);
        } else if (isValid(errors)) {
            if (isProcurement) {
                exportToProcurement();
            } else {
                navigate(ORDER_PATHS[0].link);
            }
        } else {
            showError(t('FIXCENTER_ORDERING.MESSAGE_1'));
        }
    };

    const addHiddenField = (form: HTMLFormElement, name: string, value: string | number): void => {
        const input = document.createElement('input');

        input.type = 'hidden';
        input.name = name;
        input.value = String(value);

        form.appendChild(input);
    };

    const exportToProcurement = async (): Promise<void> => {
        try {
            if (!procurementHookUrl) {
                showError(t('procurement.procurementExportError'));
                return;
            }

            const languageCode = localStorage.getItem('lang') ?? 'en';

            const { data: items } = await getProcurementExport(languageCode);

            const form = document.createElement('form');

            sessionStorage.removeItem('procurementHookUrl');

            await removeAll();

            form.method = 'POST';
            form.action = procurementHookUrl;

            items.forEach((item, index) => {
                const i = index + 1;

                addHiddenField(form, `NEW_ITEM-DESCRIPTION[${i}]`, item.description);

                addHiddenField(form, `NEW_ITEM-LONGTEXT_1[${i}]`, item.longText);

                addHiddenField(form, `NEW_ITEM-VENDORMAT[${i}]`, item.vendorMat);

                addHiddenField(form, `NEW_ITEM-CURRENCY[${i}]`, item.currency);

                addHiddenField(form, `NEW_ITEM-UNIT[${i}]`, item.unit);

                addHiddenField(form, `NEW_ITEM-PRICEUNIT[${i}]`, item.priceUnit);

                addHiddenField(form, `NEW_ITEM-QUANTITY[${i}]`, item.quantity);

                addHiddenField(form, `NEW_ITEM-PRICE[${i}]`, item.price);

                addHiddenField(form, `NEW_ITEM-MATGROUP[${i}]`, item.matGroup);
            });

            document.body.appendChild(form);

            console.log(form.outerHTML);

            form.submit();
        } catch (error) {
            showError(t('procurement.procurementExportError'));
        }
    };

    const getBuildingSiteValue = () => {
        if (selectedBuildingSite) {
            const streetName: string = selectedBuildingSite.street ?? '';
            const city: string = selectedBuildingSite.city ?? '';
            const postalCode: string = selectedBuildingSite.postalCode ?? '';
            const countryName: string = selectedBuildingSite.countryName ?? '';
            return ` ${selectedBuildingSite.name}   (${selectedBuildingSite.code})\n ${streetName}\n ${postalCode} ${city}\n ${t(countryName)}`;
        }
        return '';
    };

    const getBuildingSiteAddressValue = () => {
        if (buildingSiteAddress) {
            return ` ${buildingSiteAddress.streetName}\n ${buildingSiteAddress.postalCode} ${buildingSiteAddress.city}\n ${t(buildingSiteAddress.countryName)}`;
        }
        return '';
    };

    const getBuyerContactValue = () => {
        if (buyerContact) {
            const name: string =
                buyerContact.firstName && buyerContact.firstName.length > 0
                    ? buyerContact.firstName
                    : buyerContact.lastName;
            return ` ${name} \n ${buyerContact.email}\n ${buyerContact.phone}\n ${''}`;
        }
        return '';
    };

    const validationSchema = yup.object<CartOrderFormViewModel>().shape({
        reference: yup.string().min(Validator.minLengthName).max(Validator.maxLengthName),
        comments: yup.string().max(Validator.textAreaLengthLimit),
    });

    const isValid = (errors: FormikErrors<CartOrderFormViewModel>): boolean => {
        return Object.keys(errors).length === 0 && (isReadyToOrder as boolean);
    };

    const selectedBuildingSiteAdressExist = !!getBuildingSiteAddressValue();
    const selectedBuildingSiteExist = !!getBuildingSiteValue();
    const selectedBuildingSiteBuyerContactExist = !!getBuyerContactValue();

    return (
        <div>
            <ErrorAlert
                isOpen={permissionAlertIsOpen}
                handleCloseDialog={(val: boolean) => setOpenPermissionAlert(val)}
                translateCode={getCommonErrorTranslate(ErpClaimCode[ErpClaimCode.CUSTOMER_ECOM_ROLE])}
            ></ErrorAlert>
            <Formik
                validateOnMount
                enableReinitialize
                initialValues={initialFormValues}
                validationSchema={validationSchema}
                onSubmit={() => {}}
            >
                {({ values, handleChange, errors }) => (
                    <Form
                        autoComplete="off"
                        spellCheck="false"
                        className={styles.form}
                        onMouseEnter={() => {}}
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            order(errors);
                        }}
                    >
                        {isSkeletonLoader ? (
                            <BuildingSitesListSkeleton />
                        ) : (
                            <div>
                                <section className={classNames(globalStyles.float_right_item, globalStyles.mb_3)}>
                                    <ButtonGroupList
                                        buttons={actionButtons}
                                        onClick={(id: string) => {
                                            handleActionButtonClick(id, errors);
                                        }}
                                    />
                                </section>

                                <Box className={styles.form_input_content}>
                                    <Box className={styles.two_columns}>
                                        <Box className="column">
                                            <Typography className={styles.input_label}>
                                                {t('SELECTED_BUILDING_SITE')}*
                                            </Typography>
                                            <TextField
                                                id={`${htmlId}-building-site-input`}
                                                className={styles.text_field}
                                                onClick={() => {
                                                    setOpenBuildingSiteSelector(true);
                                                }}
                                                value={getBuildingSiteValue()}
                                                variant="outlined"
                                                multiline
                                                label={!selectedBuildingSiteExist ? t('SELECT_BUILDING_SITE') : ''}
                                                InputProps={{
                                                    style: { cursor: 'pointer' },
                                                    readOnly: true,
                                                    endAdornment: (
                                                        <InputAdornment
                                                            position="end"
                                                            sx={{ cursor: 'pointer' }}
                                                            onClick={() => {
                                                                setOpenBuildingSiteSelector(true);
                                                            }}
                                                        >
                                                            {!selectedBuildingSiteExist ? (
                                                                <ArrowDropDownIcon />
                                                            ) : (
                                                                <EditIcon sx={{ fontSize: '20px' }} />
                                                            )}
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                name="buildingSite"
                                                size="small"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: `${!selectedBuildingSiteExist ? 'red' : ''}`,
                                                        },
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        whiteSpace: 'pre-wrap',
                                                        cursor: 'pointer',
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        border: selectedBuildingSiteExist ? 'none' : undefined,
                                                    },
                                                    backgroundColor: selectedBuildingSiteExist
                                                        ? '#fafafa'
                                                        : 'transparent',
                                                }}
                                                fullWidth
                                            />
                                            <React.Fragment>
                                                <BuildingSitesSelector
                                                    isOpen={buildingSiteSelectorIsOpen}
                                                    handleCloseDialog={(value: IBuildingSiteListViewDto | null) => {
                                                        handleCloseBuildigSiteDialog(value);
                                                    }}
                                                />
                                            </React.Fragment>
                                        </Box>

                                        <Box className="column">
                                            <Typography className={styles.input_label}>
                                                {t('DELIVERY_CONTACT')}*
                                            </Typography>
                                            <TextField
                                                id={`${htmlId}-buyer-contact-input`}
                                                multiline
                                                className={styles.text_field}
                                                onClick={() => {
                                                    if (Utils.defaultUuid !== userSelection?.buildingSiteUuid) {
                                                        setOpenBuyerContactSelector(true);
                                                    }
                                                }}
                                                value={getBuyerContactValue()}
                                                variant="outlined"
                                                label={
                                                    !selectedBuildingSiteBuyerContactExist
                                                        ? t('SELECT_DELIVERY_CONTACT')
                                                        : ''
                                                }
                                                InputProps={{
                                                    style: { cursor: 'pointer' },
                                                    readOnly: true,
                                                    endAdornment: (
                                                        <InputAdornment
                                                            position="end"
                                                            sx={{ cursor: 'pointer' }}
                                                            onClick={() => {
                                                                if (
                                                                    Utils.defaultUuid !==
                                                                    userSelection?.buildingSiteUuid
                                                                ) {
                                                                    setOpenBuyerContactSelector(true);
                                                                }
                                                            }}
                                                        >
                                                            {!selectedBuildingSiteBuyerContactExist ? (
                                                                <ArrowDropDownIcon />
                                                            ) : (
                                                                <EditIcon sx={{ fontSize: '20px' }} />
                                                            )}
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                name="buyerContact"
                                                size="small"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: `${!selectedBuildingSiteBuyerContactExist ? 'red' : ''}`,
                                                        },
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        whiteSpace: 'pre-wrap',
                                                        cursor: 'pointer',
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        border: selectedBuildingSiteBuyerContactExist
                                                            ? 'none'
                                                            : undefined,
                                                    },
                                                    backgroundColor: selectedBuildingSiteBuyerContactExist
                                                        ? '#fafafa'
                                                        : 'transparent',
                                                }}
                                                disabled={Utils.defaultUuid === userSelection?.buildingSiteUuid}
                                                fullWidth
                                            />
                                            {userSelection && (
                                                <BuildingSitesDeliveryContactSelector
                                                    isOpen={buyerContactSelectorIsOpen}
                                                    selectedBuildingSiteUuid={userSelection.buildingSiteUuid}
                                                    handleCloseDialog={(value: IBuyerContactListView | null) => {
                                                        handleCloseBuyerContactDialog(value);
                                                    }}
                                                ></BuildingSitesDeliveryContactSelector>
                                            )}
                                        </Box>
                                    </Box>

                                    <Box className={styles.two_columns}>
                                        <Box className="column">
                                            <Typography className={styles.input_label}>
                                                {t('DELIVERY_ADDRESS')}*
                                            </Typography>
                                            <TextField
                                                id={`${htmlId}-building-site-address-input`}
                                                className={styles.text_field}
                                                multiline
                                                onClick={() => {
                                                    if (Utils.defaultUuid !== userSelection?.buildingSiteUuid) {
                                                        setOpenBuildingSiteAddressesSelector(true);
                                                    }
                                                }}
                                                value={getBuildingSiteAddressValue()}
                                                variant="outlined"
                                                label={
                                                    !selectedBuildingSiteAdressExist ? t('SELECT_DELIVERY_ADDRESS') : ''
                                                }
                                                InputProps={{
                                                    style: { cursor: 'pointer' },
                                                    readOnly: true,
                                                    endAdornment: (
                                                        <InputAdornment
                                                            position="end"
                                                            sx={{ cursor: 'pointer' }}
                                                            onClick={() => {
                                                                if (
                                                                    Utils.defaultUuid !==
                                                                    userSelection?.buildingSiteUuid
                                                                ) {
                                                                    setOpenBuildingSiteAddressesSelector(true);
                                                                }
                                                            }}
                                                        >
                                                            {!selectedBuildingSiteAdressExist ? (
                                                                <ArrowDropDownIcon />
                                                            ) : (
                                                                <EditIcon sx={{ fontSize: '20px' }} />
                                                            )}
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                name="buildingSiteAddress"
                                                size="small"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: `${(!isSelfPickup ? !selectedBuildingSiteAdressExist : false) ? 'red' : ''}`,
                                                        },
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        whiteSpace: 'pre-wrap',
                                                        cursor: 'pointer',
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        border: selectedBuildingSiteAdressExist ? 'none' : undefined,
                                                    },
                                                    backgroundColor: selectedBuildingSiteAdressExist
                                                        ? '#fafafa'
                                                        : 'transparent',
                                                }}
                                                disabled={Utils.defaultUuid === userSelection?.buildingSiteUuid}
                                                fullWidth
                                            />
                                            {userSelection && (
                                                <BuildingSitesDeliveryAddressSelector
                                                    isOpen={buildingSiteAddressesSelectorIsOpen}
                                                    selectedBuildingSiteUuid={userSelection.buildingSiteUuid}
                                                    handleCloseDialog={(value: IBuildingSiteAddressListView | null) => {
                                                        handleCloseBuildigSiteAddressDialog(value);
                                                    }}
                                                ></BuildingSitesDeliveryAddressSelector>
                                            )}
                                        </Box>

                                        <Box className="column">
                                            <Typography className={styles.input_label}>{t('REFERENCE')}</Typography>
                                            <TextField
                                                id={`${htmlId}-reference-input`}
                                                className={styles.text_field}
                                                onChange={handleChange}
                                                onBlur={() => handlUpdateOrAddDetails(values, isSelfPickup as boolean)}
                                                value={values.reference}
                                                variant="outlined"
                                                name="reference"
                                                error={!!errors.reference}
                                                helperText={errors.reference || ''}
                                                size="small"
                                                fullWidth
                                            />
                                        </Box>
                                    </Box>
                                    <Box className={styles.two_columns}>
                                        <Box className="column">
                                            <Typography className={styles.input_label}>{t('COMMENTS')}</Typography>
                                            <TextField
                                                id={`${htmlId}-comments-textarea`}
                                                className={styles.text_field}
                                                variant="outlined"
                                                name="comments"
                                                error={!!errors.comments}
                                                helperText={errors.comments || ''}
                                                value={values.comments}
                                                onChange={handleChange}
                                                onBlur={() => handlUpdateOrAddDetails(values, isSelfPickup as boolean)}
                                                fullWidth
                                                multiline
                                                rows={3}
                                            />
                                        </Box>
                                        <Box className="column">
                                            <Typography className={styles.input_label}>{t('ISSELFPICKUP')}</Typography>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        disableFocusRipple
                                                        disableRipple
                                                        id={`${htmlId}-isselfpickup-input`}
                                                        name="isselfpickup"
                                                        checked={isSelfPickup}
                                                        onChange={(e) => handleIsSelfPickup(e, values)}
                                                        sx={{
                                                            paddingTop: 0,
                                                            marginTop: 0,
                                                        }}
                                                        icon={
                                                            <span
                                                                style={{
                                                                    width: 20,
                                                                    height: 20,
                                                                    border: '2px solid #821818',
                                                                    borderRadius: 4,
                                                                    display: 'inline-block',
                                                                }}
                                                            />
                                                        }
                                                        checkedIcon={
                                                            <span
                                                                style={{
                                                                    width: 20,
                                                                    height: 20,
                                                                    backgroundColor: '#821818',
                                                                    borderRadius: 4,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                <CheckIcon style={{ color: 'white', fontSize: 18 }} />
                                                            </span>
                                                        }
                                                    />
                                                }
                                                label=""
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                                <CartBasketTable />
                                <section
                                    className={classNames(
                                        globalStyles.float_right_item,
                                        globalStyles.mb_1,
                                        globalStyles.mb_2
                                    )}
                                >
                                    <ButtonGroupList
                                        buttons={actionButtons}
                                        onClick={(id: string) => {
                                            handleActionButtonClick(id, errors);
                                        }}
                                    />
                                </section>
                            </div>
                        )}
                    </Form>
                )}
            </Formik>
        </div>
    );
};
export default CartOrderForm;
